import * as React from "react"
import Link from 'next/link'
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from '@/lib/utils'
import { ArrowUpRight } from 'lucide-react'

const enhancedButtonVariants = cva(
  "inline-flex items-center gap-3 rounded-full font-medium transition-all duration-300 disabled:pointer-events-none disabled:opacity-50 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 group",
  {
    variants: {
      variant: {
        default: "bg-foreground text-background",
        inverse: "bg-background text-foreground border border-border",
      },
      size: {
        sm: "pl-3 pr-3 py-2 text-sm",
        default: "pl-4 pr-2 py-2 text-sm",
        lg: "pl-6 pr-2 py-2 text-base",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "lg",
    },
  }
)

const sizeConfig = {
  sm: { circleSize: "w-6 h-6", iconSize: 14 },
  default: { circleSize: "w-7 h-7", iconSize: 16 },
  lg: { circleSize: "w-11 h-11", iconSize: 20 },
}

interface EnhancedButtonProps extends React.ComponentProps<"button">, VariantProps<typeof enhancedButtonVariants> {
  href?: string
  asChild?: boolean
  fullWidth?: boolean
  centerText?: boolean
}

export default function EnhancedButton({ 
  children, 
  href, 
  className,
  variant = "default",
  size = "lg",
  asChild = false,
  fullWidth = false,
  centerText = true,
  ...props
}: EnhancedButtonProps) {
  const config = sizeConfig[size as keyof typeof sizeConfig];
  const { circleSize, iconSize } = config;
  
  const circleColors = variant === "inverse" 
    ? "bg-foreground text-background" 
    : "bg-background text-foreground";
  
  const circle = (
    <span 
      className={cn(
        "flex items-center justify-center flex-shrink-0 rounded-full",
        circleColors,
        "transition-all duration-300 ease-out",
        "group-hover:scale-105",
        fullWidth && centerText && "ml-auto relative z-10",
        circleSize
      )}
    >
      <ArrowUpRight 
        className={cn(
          "transition-transform duration-300 ease-out",
          "group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        )}
        size={iconSize}
      />
    </span>
  );

  const content = (
    <>
      <span className={cn(
        "flex-1",
        fullWidth && centerText && "absolute inset-0 flex items-center justify-center",
        centerText ? "text-center" : "text-left"
      )}>
        {children}
      </span>
      {circle}
    </>
  );

  const buttonClasses = cn(
    enhancedButtonVariants({ variant, size }), 
    fullWidth && "relative w-full",
    !centerText && "justify-between",
    className
  );

  if (href && !asChild) {
    return (
      <Link 
        href={href} 
        className={buttonClasses}
      >
        {content}
      </Link>
    );
  }

  if (asChild) {
    return (
      <Slot 
        className={buttonClasses}
        {...props}
      >
        {content}
      </Slot>
    );
  }

  return (
    <button 
      className={buttonClasses}
      {...props}
    >
      {content}
    </button>
  );
}
